import { SubscriptionsManager } from 'src/app/shared/helpers/subscriptions-manager';
import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { TourService, TourStep } from '../../services/tour.service';
import { TourConfigService } from '../../services/tour-config.service';
import { UserSettingsService } from '../../services/user-settings.service';
import { Store } from '@ngrx/store';
import { AppState } from '../../stores/states/app.state';
import * as UserActions from '../../stores/actions/user.actions';
import { fromEvent, take } from 'rxjs';

@Component({
  selector: 'app-tour-notification',
  templateUrl: './tour-notification.component.html',
  styleUrls: ['./tour-notification.component.scss']
})
export class TourNotificationComponent implements OnInit, OnDestroy {
  @Input() tourSteps: TourStep[] = [];
  @Input() currentUser: any = null;
  @Input() notificationTitle: string = 'News Feed Tour';
  @Input() notificationIcon: string = 'rss';
  @Input() notificationMessage: string = 'Want to learn more about the features? Start a quick tour to explore the interface.';
  @Input() notificationDescription: string = 'Discover how to navigate the dashboard, manage boards, and use AI-powered features to summarize content, answer questions, and explore information.';
  @Input() notificationId: string = 'default-tour-notification';
  @Input() showResetOption: boolean = true;

  isVisible = true;
  isExpanded = true;
  isPermanentlyHidden = false;
  private autoHideTimeout?: number;

  constructor(
    private tourService: TourService,
    private tourConfigService: TourConfigService,
    private userSettingsService: UserSettingsService,
    private store: Store<AppState>,
    private subscriptionsManager: SubscriptionsManager
  ) { }

  ngOnInit(): void {
    this.isPermanentlyHidden = localStorage.getItem(`${this.notificationId}-hidden`) === 'true';
  }

  ngOnDestroy(): void {
    if (this.autoHideTimeout) {
      clearTimeout(this.autoHideTimeout);
      this.autoHideTimeout = undefined;
    }
    this.subscriptionsManager.unsubscribeAll();
  }

  toggleExpanded(): void {
    this.isExpanded = !this.isExpanded;
  }

  hideNotification(): void {
    this.isVisible = false;
  }

  async hideNotificationPermanently(event?: Event) {
    const menuPanel = document.querySelector<HTMLElement>('#userMenu');
    menuPanel?.click();
    if (menuPanel) {
      await new Promise<void>(resolve => {
        fromEvent(menuPanel, 'transitionend')
          .pipe(take(1))
          .subscribe(() => resolve());
      });
    }

    const tourSteps = this.tourConfigService.getDoNotShowAgainButtonTour();
    this.tourService.initTour(tourSteps);
    this.tourService.start();
    // im using this so i can close the menu and hide the notification after the tour has finished. It makes it look cleaner.
    this.subscriptionsManager.add(this.tourService.tourFinished$.subscribe({
      next: () => {
        menuPanel?.click();
        this.hideNotification();  
      }
    }));

    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    this.isPermanentlyHidden = true;
    this.isVisible = false;
    localStorage.setItem(`${this.notificationId}-hidden`, 'true');

  }

  showNotification(): void {

    this.isPermanentlyHidden = false;
    this.isVisible = true;
    localStorage.removeItem(`${this.notificationId}-hidden`);

  }

  startTour(): void {


    if (this.tourSteps.length > 0) {

      this.tourService.initTour(this.tourSteps);
      this.tourService.start();
    } else {

      const tourSteps = this.tourConfigService.getDashboardTourSteps();
      this.tourService.initTour(tourSteps);
      this.tourService.start();
    }
    this.hideNotification();
  }


  hasSeenTour(): boolean {
    return this.currentUser?.settings?.showTourOnFirstVisit === false;
  }


  async resetTourSetting(): Promise<void> {
    try {

      const userSettings = this.currentUser?.settings || {};
      await this.userSettingsService.save({
        ...userSettings,
        showTourOnFirstVisit: true,
      });


      this.store.dispatch(UserActions.updateUserSettings({
        settings: { ...userSettings, showTourOnFirstVisit: true }
      }));

      this.currentUser = {
        ...this.currentUser,
        settings: { ...userSettings, showTourOnFirstVisit: true }
      };
    } catch (error) {
      console.error('Error resetting tour setting:', error);
    }
  }


  getNotificationStatus(): any {
    return {
      isPermanentlyHidden: this.isPermanentlyHidden,
      isVisible: this.isVisible,
      notificationId: this.notificationId,
      localStorageValue: localStorage.getItem(`${this.notificationId}-hidden`),
      userSettings: this.currentUser?.settings?.showTourOnFirstVisit
    };
  }
}
